import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  Progress,
  Spin,
  Modal,
  Tag,
  Empty,
  notification
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  LeftOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  getQuizByContentId,
  submitQuizAttempt,
  getCourseById
} from '../../../../../Constants/Api/functions/function';
import './style.scss';

const ContentQuiz = () => {
  const { id: courseId, contentId } = useParams();
  const history = useHistory();
  const userProfile = useSelector((state) => state.userProfile.userProfile);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [course, setCourse] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attempt, setAttempt] = useState(null);

  // Runner state
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // { [questionId]: [0, 2] }
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    loadCourseAndQuiz();
  }, [courseId, contentId, userProfile]);

  const loadCourseAndQuiz = async () => {
    setIsLoading(true);
    try {
      // 1. Load course details
      const courseRes = await getCourseById(courseId);
      if (courseRes) {
        setCourse(Array.isArray(courseRes) ? courseRes[0] : courseRes);
      }

      // 2. Load quiz data
      if (contentId) {
        const quizRes = await getQuizByContentId(
          contentId,
          userProfile?.id,
          userProfile?.role
        );
        if (quizRes && quizRes.success) {
          setQuiz(quizRes.quiz);
          setQuestions(quizRes.questions || []);
          if (quizRes.attempt) {
            setAttempt(quizRes.attempt);
          }
        }
      }
    } catch (error) {
      console.error('Error loading quiz page:', error);
    }
    setIsLoading(false);
  };

  const handleStartQuiz = () => {
    setIsQuizStarted(true);
    setCurrentIndex(0);
    setUserAnswers({});
  };

  const handleSelectOption = (questionId, optIndex, questionType) => {
    setUserAnswers((prev) => {
      const currentSelected = prev[questionId] || [];
      if (questionType === 'multiple') {
        if (currentSelected.includes(optIndex)) {
          return {
            ...prev,
            [questionId]: currentSelected.filter((i) => i !== optIndex)
          };
        } else {
          return {
            ...prev,
            [questionId]: [...currentSelected, optIndex]
          };
        }
      } else {
        // Single choice
        return {
          ...prev,
          [questionId]: [optIndex]
        };
      }
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSubmitConfirm = () => {
    const answeredCount = Object.keys(userAnswers).filter(
      (qId) => userAnswers[qId] && userAnswers[qId].length > 0
    ).length;

    Modal.confirm({
      title: 'Submit Quiz Confirmation',
      icon: <ExclamationCircleOutlined style={{ color: '#6366f1' }} />,
      content: (
        <div>
          <p>
            You have answered <strong>{answeredCount}</strong> of{' '}
            <strong>{questions.length}</strong> questions.
          </p>
          <p style={{ color: '#e11d48', fontWeight: 600 }}>
            ⚠️ Strict 1-attempt policy: Once submitted, you cannot change your answers or retake this quiz.
          </p>
          <p>Are you sure you want to finalize and submit?</p>
        </div>
      ),
      okText: 'Yes, Submit Quiz',
      cancelText: 'Review Questions',
      onOk: performSubmit
    });
  };

  const performSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        quizId: quiz.id,
        courseContentId: contentId,
        courseId: courseId,
        userId: userProfile?.id,
        answers: userAnswers
      };

      const res = await submitQuizAttempt(payload);
      if (res && res.success) {
        api.success({
          message: 'Quiz Completed!',
          description: `Your score: ${res.attempt.score} / ${res.attempt.totalMarks} (${res.attempt.percentage}%)`
        });
        setAttempt(res.attempt);
        setIsQuizStarted(false);
        // Reload to update correct answers in review
        await loadCourseAndQuiz();
      } else {
        api.error({
          message: 'Submission Failed',
          description: res.message || 'Could not submit quiz attempt'
        });
      }
    } catch (err) {
      console.error('Quiz submit error:', err);
      api.error({
        message: 'Submission Error',
        description: 'An unexpected error occurred while submitting.'
      });
    }
    setIsSubmitting(false);
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return '#16a34a';
      case 'B':
        return '#2563eb';
      case 'C':
        return '#d97706';
      case 'S':
        return '#0284c7';
      default:
        return '#dc2626';
    }
  };

  if (isLoading) {
    return (
      <div className="content-quiz-page" style={{ textAlign: 'center', paddingTop: 100 }}>
        <Spin size="large" tip="Loading Quiz..." />
      </div>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <div className="content-quiz-page">
        <div className="quiz-page-header">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            className="back-btn"
            onClick={() => history.push(`/dashboard/cources/${courseId}`)}
          >
            Back to Course
          </Button>
        </div>
        <div className="quiz-intro-card">
          <Empty
            description={
              <div>
                <h2>No Quiz Available</h2>
                <p>There is currently no quiz configured for this lesson.</p>
              </div>
            }
          >
            <Button
              type="primary"
              onClick={() => history.push(`/dashboard/cources/${courseId}`)}
            >
              Return to Course
            </Button>
          </Empty>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentSelectedOptions = currentQuestion ? userAnswers[currentQuestion.id] || [] : [];
  const progressPercent = parseFloat((((currentIndex + 1) / questions.length) * 100).toFixed(0));

  return (
    <div className="content-quiz-page">
      {contextHolder}

      {/* Breadcrumb Header */}
      <div className="quiz-page-header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          className="back-btn"
          onClick={() => history.push(`/dashboard/cources/${courseId}`)}
        >
          Back to Course
        </Button>

        <div className="course-breadcrumb">
          <span className="course-name">{course?.name}</span>
          <h1 className="content-title">
            {quiz?.title || 'Course Content Quiz'}
          </h1>
        </div>
      </div>

      {/* STATE 1: ALREADY COMPLETED (Score Screen & Review) */}
      {attempt ? (
        <div className="quiz-result-card">
          <div className="result-hero">
            <span className="trophy-icon">🏆</span>
            <h2>Quiz Results & Score</h2>
            <p className="result-subtitle">
              Submitted on {new Date(attempt.submittedAt).toLocaleDateString()} at{' '}
              {new Date(attempt.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>

            <div className="score-metrics">
              <div className="metric-card">
                <span className="label">Score Obtained</span>
                <span className="value">
                  {parseFloat(attempt.score).toFixed(1)} / {parseFloat(attempt.totalMarks).toFixed(1)}
                </span>
              </div>

              <div className="metric-card">
                <span className="label">Percentage</span>
                <span className="value">{attempt.percentage}%</span>
              </div>

              <div className="metric-card highlight-grade">
                <span className="label">Calculated Grade</span>
                <span className="value" style={{ color: getGradeColor(attempt.grade) }}>
                  {attempt.grade || 'Pass'}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <Tag color="purple" style={{ fontSize: '0.9rem', padding: '6px 14px', borderRadius: 8 }}>
                ✓ Single attempt completed. Your grade has been recorded in your course progress.
              </Tag>
            </div>
          </div>

          {/* Question Review Section */}
          <div className="review-section">
            <h3>
              <QuestionCircleOutlined /> Review Your Answers
            </h3>

            <div className="review-questions-list">
              {questions.map((q, qIdx) => {
                const studentAns = (attempt.answers && attempt.answers[q.id]) || [];
                const correctAns = Array.isArray(q.correctAnswers) ? q.correctAnswers : [];
                const qMarks = parseFloat(q.marks) || 1;
                const isMultiple = q.questionType === 'multiple' || correctAns.length > 1;

                let earnedMarks = 0;
                let isExactMatch = false;

                if (isMultiple) {
                  if (correctAns.length > 0 && studentAns.length > 0) {
                    const markPerCorrect = qMarks / correctAns.length;
                    const correctCount = studentAns.filter((c) => correctAns.includes(c)).length;

                    if (studentAns.length >= 5 && correctAns.length < 5) {
                      earnedMarks = 0;
                    } else {
                      earnedMarks = Math.min(qMarks, parseFloat((correctCount * markPerCorrect).toFixed(2)));
                    }

                    isExactMatch = correctCount === correctAns.length && studentAns.length === correctAns.length;
                  }
                } else {
                  isExactMatch =
                    correctAns.length > 0 &&
                    studentAns.length === 1 &&
                    correctAns.includes(studentAns[0]);
                  earnedMarks = isExactMatch ? qMarks : 0;
                }

                const isPartial = !isExactMatch && earnedMarks > 0;

                return (
                  <div
                    key={q.id || qIdx}
                    className={`review-q-card ${isExactMatch ? 'passed' : isPartial ? 'partial' : 'failed'}`}
                  >
                    <div className="q-header-row">
                      <div className="q-title-wrap">
                        <span>Q{qIdx + 1}. </span>
                        <span>{q.questionText}</span>
                      </div>
                      <div className="q-status-badge">
                        {isExactMatch ? (
                          <Tag color="success" icon={<CheckCircleOutlined />}>
                            Correct (+{earnedMarks} Marks)
                          </Tag>
                        ) : isPartial ? (
                          <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                            Partial Credit (+{earnedMarks}/{qMarks} Marks)
                          </Tag>
                        ) : (
                          <Tag color="error" icon={<CloseCircleOutlined />}>
                            Incorrect (0/{qMarks} Marks)
                          </Tag>
                        )}
                      </div>
                    </div>

                    <div className="review-options">
                      {[q.option1, q.option2, q.option3, q.option4, q.option5].map((optText, optIdx) => {
                        const isStudentChoice = studentAns.includes(optIdx);
                        const isCorrectChoice = correctAns.includes(optIdx);

                        let optClass = '';
                        if (isCorrectChoice) optClass = 'correct-answer';
                        else if (isStudentChoice && !isCorrectChoice) optClass = 'wrong-selected';

                        return (
                          <div key={optIdx} className={`review-opt-row ${optClass}`}>
                            <span className="opt-badge">{String.fromCharCode(65 + optIdx)}</span>
                            <span className="opt-text">{optText}</span>

                            {isCorrectChoice && isStudentChoice && (
                              <span className="tag-status" style={{ background: '#22c55e', color: '#fff' }}>
                                ✓ Your Correct Answer
                              </span>
                            )}
                            {isCorrectChoice && !isStudentChoice && (
                              <span className="tag-status" style={{ background: '#10b981', color: '#fff' }}>
                                Correct Answer
                              </span>
                            )}
                            {!isCorrectChoice && isStudentChoice && (
                              <span className="tag-status" style={{ background: '#ef4444', color: '#fff' }}>
                                ✗ Your Incorrect Choice
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {q.explanation && (
                      <div className="explanation-callout">
                        <strong>Explanation: </strong> {q.explanation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Button
                type="primary"
                size="large"
                shape="round"
                onClick={() => history.push(`/dashboard/cources/${courseId}`)}
              >
                Return to Course Lessons
              </Button>
            </div>
          </div>
        </div>
      ) : !isQuizStarted ? (
        /* STATE 2: INTRO CARD BEFORE START */
        <div className="quiz-intro-card">
          <span className="intro-icon">📝</span>
          <h1>{quiz.title || 'Lesson Quiz'}</h1>
          <p className="intro-desc">
            Test your knowledge of this course content. Please read each question carefully.
            Questions will be displayed <strong>one by one</strong>.
          </p>

          <div className="quiz-rules-grid">
            <div className="rule-item">
              <span className="rule-icon">❓</span>
              <div className="rule-info">
                <span className="title">Questions</span>
                <span className="val">{questions.length} Items</span>
              </div>
            </div>

            <div className="rule-item">
              <span className="rule-icon">⭐</span>
              <div className="rule-info">
                <span className="title">Total Marks</span>
                <span className="val">{quiz.totalMarks || questions.length} Points</span>
              </div>
            </div>

            <div className="rule-item">
              <span className="rule-icon">🔒</span>
              <div className="rule-info">
                <span className="title">Attempt Limit</span>
                <span className="val">1 Attempt Only</span>
              </div>
            </div>

            <div className="rule-item">
              <span className="rule-icon">🎯</span>
              <div className="rule-info">
                <span className="title">Passing Score</span>
                <span className="val">{quiz.passingPercentage || 50}%</span>
              </div>
            </div>
          </div>

          <Button
            type="primary"
            size="large"
            className="start-quiz-btn"
            onClick={handleStartQuiz}
          >
            Start Quiz Now
          </Button>
        </div>
      ) : (
        /* STATE 3: ONE-BY-ONE QUESTION TAKER RUNNER */
        <div className="quiz-runner-card">
          <div className="runner-top-bar">
            <div className="runner-progress-header">
              <div className="q-counter">
                <span>Question</span>
                <span className="badge-pill">
                  {currentIndex + 1} of {questions.length}
                </span>
              </div>
              <div className="q-marks-pill">
                ⭐ {currentQuestion?.marks} {Number(currentQuestion?.marks) === 1 ? 'Mark' : 'Marks'}
              </div>
            </div>
            <Progress
              percent={progressPercent}
              showInfo={false}
              strokeColor={{ from: '#4f46e5', to: '#7c3aed' }}
            />
          </div>

          <div className="question-body">
            <h2 className="question-prompt">{currentQuestion?.questionText}</h2>

            <div className="instruction-hint">
              {currentQuestion?.questionType === 'multiple'
                ? 'ℹ️ Multiple Choice: Select all answers that are correct.'
                : 'ℹ️ Single Choice: Select the one correct answer.'}
            </div>

            <div className="answers-container">
              {[
                currentQuestion?.option1,
                currentQuestion?.option2,
                currentQuestion?.option3,
                currentQuestion?.option4,
                currentQuestion?.option5
              ].map((optText, optIdx) => {
                const isSelected = currentSelectedOptions.includes(optIdx);
                const letter = String.fromCharCode(65 + optIdx);

                return (
                  <div
                    key={optIdx}
                    className={`answer-choice-btn ${isSelected ? 'selected' : ''}`}
                    onClick={() =>
                      handleSelectOption(
                        currentQuestion.id,
                        optIdx,
                        currentQuestion.questionType
                      )
                    }
                  >
                    <div className="opt-letter">{letter}</div>
                    <div className="opt-text">{optText}</div>
                    <div className="select-indicator">
                      {isSelected ? (
                        <CheckCircleOutlined style={{ color: '#4f46e5', fontSize: '1.2rem' }} />
                      ) : (
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: currentQuestion.questionType === 'multiple' ? 4 : '50%',
                            border: '2px solid #cbd5e1'
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="runner-footer">
            <Button
              icon={<LeftOutlined />}
              disabled={currentIndex === 0}
              onClick={handlePrev}
            >
              Previous
            </Button>

            {!isLastQuestion ? (
              <Button type="primary" onClick={handleNext}>
                Next Question <RightOutlined />
              </Button>
            ) : (
              <Button
                type="primary"
                loading={isSubmitting}
                onClick={handleSubmitConfirm}
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  borderColor: '#059669',
                  fontWeight: 600
                }}
              >
                Submit Quiz
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentQuiz;
